import { Fragment, ReactNode } from "react";
import { Typography, IconButton } from "@/components/kit";
import { Dialog, Transition } from "@headlessui/react";
import classNames from "classnames";
import {
  Popover,
  PopoverContent,
  PopoverHandler,
} from "@material-tailwind/react";

export type TDialogProps = {
  open: boolean;
  isCloseable?: boolean;
  handler: React.Dispatch<React.SetStateAction<any>>;
  title: ReactNode | string;
  body?: ReactNode | string;
  children?: ReactNode;
  mainClassName?: string;
  titleClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  width?: `${string}px`;
  popoverContent?: ReactNode;
  backdropProps?: { onClick?: () => void };
};

function MyDialog({
  open,
  isCloseable = true,
  handler,
  title,
  body,
  width = "448px",
  children,
  titleClassName = "",
  mainClassName = "",
  bodyClassName = "",
  footerClassName = "",
  popoverContent,
  backdropProps,
}: Readonly<TDialogProps>) {
  return (
    <Transition appear show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-[var(--dialog-zIndex)]"
        onClose={handler}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 bg-black/60"
            onClick={
              backdropProps?.onClick ||
              (!isCloseable ? (e) => e.stopPropagation() : handler)
            } // Handle backdrop click
          />
        </Transition.Child>
        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel
                className={classNames(
                  `${mainClassName} transform rounded-[8px] bg-white p-4 text-left align-middle shadow-xl transition-all`,
                )}
                style={{
                  width,
                }}
              >
                <Dialog.Title
                  className={`${titleClassName} flex items-center justify-between`}
                >
                  <div className={"flex items-center gap-2"}>
                    <Typography variant="Title5-Bold">{title}</Typography>
                    {!!popoverContent && (
                      <Popover placement="bottom-start">
                        <PopoverHandler>
                          <IconButton
                            iconName={"info"}
                            placeholder=""
                            className="p-0 h-4 w-4"
                            variant="text"
                            color={"gray"}
                          />
                        </PopoverHandler>
                        <PopoverContent
                          placeholder=""
                          className="dark:bg-blue-gray-800 border-0 z-[var(--poover-zIndex)]"
                        >
                          <div className="w-[350px] flex flex-col gap-1">
                            <Typography variant="Body1-Medium">
                              {popoverContent}
                            </Typography>
                          </div>
                        </PopoverContent>
                      </Popover>
                    )}
                  </div>
                  {isCloseable && (
                    <IconButton
                      iconName="close"
                      onClick={handler}
                      variant="text"
                      svgClassName="w-7 h-7"
                      className="w-8 h-8"
                      color="gray"
                    />
                  )}
                </Dialog.Title>
                {body && (
                  <div className="">
                    <div
                      className={`p-0 mt-2 mb-2 text-gray-800 font-medium ${bodyClassName}`}
                    >
                      {body}
                    </div>
                  </div>
                )}
                {children && (
                  <div
                    className={
                      footerClassName?.includes("override")
                        ? footerClassName
                        : `flex items-center justify-end shrink-0 flex-wrap gap-2 ${footerClassName}`
                    }
                  >
                    {children}
                  </div>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

export default MyDialog;
